import React, {
  useState,
  useEffect,
  useContext,
  useCallback,
  useMemo,
} from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import Select, { components } from 'react-select';
import { useLazyQuery, useQuery } from '@apollo/client';
import { FSBTable } from 'components/common/FSBTable';
import { authContext } from 'contexts/AuthContext';
import { RiPencilFill } from 'react-icons/ri';
import { CITY_BY_STATE, STATES_BY_COUNTRY } from 'pages/MarketListing/MarketListingGraphQL';
import MatrixDetailView from './MatrixDetailView';
import AddListView from './AddListView';
import ImportRoutesModal from './ImportRoutesModal';
import CityCheckboxFilter from './CityCheckboxFilter';
import './ListTab.css';

const INDIA_COUNTRY_ID = '101';
let cachedCityOptions = null;

const CompactValueContainer = ({ children, ...props }) => {
  const selected = props.getValue();
  const count = selected?.length || 0;

  return (
    <components.ValueContainer {...props}>
      {count > 1 && (
        <span
          title={selected.map((item) => item.label || item.cityName).join(', ')}
          style={{
            fontSize: '11px',
            fontWeight: 600,
            color: '#1d4ed8',
            background: '#eff4ff',
            border: '1px solid #c7d7fe',
            borderRadius: '4px',
            padding: '1px 6px',
            marginRight: '4px',
            whiteSpace: 'nowrap',
            lineHeight: '18px',
            display: 'inline-block',
            userSelect: 'none',
            flexShrink: 0,
          }}
        >
          {count} selected
        </span>
      )}
      {children}
    </components.ValueContainer>
  );
};

const listSelectStyles = {
  control: (base, state) => ({
    ...base,
    height: '34px',
    minHeight: '34px',
    maxHeight: '34px',
    borderColor: state.isFocused ? '#1d4ed8' : '#d0d5dd',
    boxShadow: state.isFocused ? '0 0 0 2px rgba(29,78,216,0.1)' : 'none',
    borderRadius: '6px',
    fontSize: '12px',
    background: '#fff',
    flexWrap: 'nowrap',
    overflow: 'hidden',
  }),
  valueContainer: (base) => ({
    ...base,
    padding: '0 6px',
    height: '32px',
    maxHeight: '32px',
    display: 'flex',
    flexWrap: 'nowrap',
    overflow: 'hidden',
    alignItems: 'center',
  }),
  indicatorsContainer: (base) => ({
    ...base,
    height: '32px',
  }),
  multiValue: (base, state) => ({
    ...base,
    display: state?.selectProps?.value?.length > 1 ? 'none' : 'flex',
    fontSize: '11px',
    maxWidth: '75px',
    margin: '1px 2px',
    flexShrink: 0,
  }),
  multiValueLabel: (base) => ({
    ...base,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    padding: '1px 4px',
  }),
  input: (base) => ({
    ...base,
    margin: 0,
    padding: 0,
    color: '#101828',
    visibility: 'visible',
    flex: '1 1 auto',
    minWidth: '50px',
  }),
  placeholder: (base) => ({
    ...base,
    fontSize: '12px',
    color: '#98a2b3',
    whiteSpace: 'nowrap',
  }),
  menu: (base) => ({ ...base, zIndex: 99999, minWidth: '260px' }),
  menuPortal: (base) => ({ ...base, zIndex: 99999 }),
  indicatorSeparator: () => ({ display: 'none' }),
};

const formatLocation = (cityName, stateName) => {
  const city = cityName?.trim();
  const state = stateName?.trim();
  if (!city && !state) return '—';
  if (city && state) {
    if (city.toLowerCase().includes(state.toLowerCase())) {
      return city;
    }
    return `${city} (${state})`;
  }
  return city || (state ? `(${state})` : '—');
};

/* ------------------------------------------------------------------ */
/* Table Columns Configuration                                          */
/* ------------------------------------------------------------------ */
const getListColumns = (onEdit) => [
  {
    id: 'action',
    header: 'ACTION',
    size: 82,
    enableSorting: false,
    cell: ({ row }) => (
      <button
        type="button"
        className="list-tab-edit-button"
        aria-label={`Edit route from ${row.original.originCityName} to ${row.original.destinationCityName}`}
        title="Edit route"
        onClick={(event) => {
          event.stopPropagation();
          onEdit(row.original);
        }}
      >
        <RiPencilFill size={16} aria-hidden="true" />
      </button>
    ),
  },
  {
    accessorKey: 'originCityName',
    enableSorting: false,
    header: 'ORIGIN',
    size: 180,
    cell: ({ row }) => {
      const city = row.original.originCityName ?? row.original.originCity?.cityName;
      const state = row.original.originStateName ?? row.original.originState?.stateName;
      return formatLocation(city, state);
    },
  },
  {
    accessorKey: 'destinationCityName',
    enableSorting: false,
    header: 'DESTINATION',
    size: 180,
    cell: ({ row }) => {
      const city = row.original.destinationCityName ?? row.original.destinationCity?.cityName;
      const state = row.original.destinationStateName ?? row.original.destinationState?.stateName;
      return formatLocation(city, state);
    },
  },
  {
    accessorKey: 'distanceKm',
    header: 'DISTANCE (KM)',
    cell: ({ getValue }) => getValue() || '—',
    size: 180,
  },
  {
    accessorKey: 'transitTimeDays',
    header: 'TRANSIT TIME',
    cell: ({ getValue }) => {
      const value = getValue();
      return value ? `${value}` : '—';
    },
    size: 120,
  },
  {
    accessorKey: 'ftl10Mt',
    header: '10MT FREIGHT (INR)',
    cell: ({ getValue }) => {
      const value = getValue();
      return value ? `₹${value.toLocaleString('en-IN')}` : '—';
    },
    size: 160,
  },
  {
    accessorKey: 'ftl15Mt',
    header: '15MT FREIGHT (INR)',
    cell: ({ getValue }) => {
      const value = getValue();
      return value ? `₹${value.toLocaleString('en-IN')}` : '—';
    },
    size: 160,
  },
  {
    accessorKey: 'ftl20Mt',
    header: '20MT FREIGHT (INR)',
    cell: ({ getValue }) => {
      const value = getValue();
      return value ? `₹${value.toLocaleString('en-IN')}` : '—';
    },
    size: 160,
  },
  {
    accessorKey: 'ftl25Mt',
    header: '25MT FREIGHT (INR)',
    cell: ({ getValue }) => {
      const value = getValue();
      return value ? `₹${value.toLocaleString('en-IN')}` : '—';
    },
    size: 160,
  },
  {
    accessorKey: 'ftl30Mt',
    header: '30MT FREIGHT (INR)',
    cell: ({ getValue }) => {
      const value = getValue();
      return value ? `₹${value.toLocaleString('en-IN')}` : '—';
    },
    size: 160,
  },
  {
    accessorKey: 'ftl35Mt',
    header: '35MT FREIGHT (INR)',
    cell: ({ getValue }) => {
      const value = getValue();
      return value ? `₹${value.toLocaleString('en-IN')}` : '—';
    },
    size: 160,
  },
];

const getRouteRows = (payload) => {
  if (Array.isArray(payload?.data?.content)) return payload.data.content;
  if (Array.isArray(payload?.content)) return payload.content;
  if (Array.isArray(payload?.data?.data)) return payload.data.data;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload)) return payload;
  return [];
};

const getRouteTotalCount = (payload, rows = []) =>
  payload?.pagingResponse?.totalItems ??
  payload?.pagingResponse?.totalCount ??
  payload?.pagingResponse?.totalElements ??
  payload?.data?.totalElements ??
  payload?.totalElements ??
  payload?.data?.totalCount ??
  payload?.totalCount ??
  payload?.data?.totalRecords ??
  payload?.totalRecords ??
  rows.length;

const getRouteTotalPages = (payload, pageSize, totalCount) =>
  payload?.pagingResponse?.totalPages ??
  payload?.data?.totalPages ??
  payload?.totalPages ??
  (pageSize > 0 ? Math.ceil(totalCount / pageSize) : 0);

const getApiBaseUrl = () =>
  process.env.REACT_APP_API_BASE_URL ||
  process.env.REACT_APP_REST_API_ENDPOINT?.replace(/\/$/, '');

const downloadBlob = (data, fileName) => {
  const url = window.URL.createObjectURL(data);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', fileName);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

/* ------------------------------------------------------------------ */
/* Component                                                           */
/* ------------------------------------------------------------------ */
const ListTab = ({ setHeaderActions }) => {
  const { auth } = useContext(authContext);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [filters, setFilters] = useState({
    pageNo: 1,
    pageSize: 10,
  });
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [routeToEdit, setRouteToEdit] = useState(null);
  const [isAddingRoute, setIsAddingRoute] = useState(false);
  const [originFilter, setOriginFilter] = useState([]);
  const [destinationFilter, setDestinationFilter] = useState([]);
  const [cityOptions, setCityOptions] = useState(cachedCityOptions || []);
  const [citiesLoading, setCitiesLoading] = useState(!cachedCityOptions);

  const { data: statesData, loading: statesLoading } = useQuery(STATES_BY_COUNTRY, {
    variables: { country: INDIA_COUNTRY_ID },
    fetchPolicy: 'cache-first',
  });
  const [fetchCitiesByState] = useLazyQuery(CITY_BY_STATE, { fetchPolicy: 'cache-first' });

  useEffect(() => {
    if (cachedCityOptions?.length) {
      setCityOptions(cachedCityOptions);
      setCitiesLoading(false);
      return undefined;
    }

    const states = statesData?.statesByCountry || [];
    if (!states.length) return undefined;
    let isMounted = true;

    const loadCities = async () => {
      try {
        const cityGroups = await Promise.all(states.map(async (state) => {
          const response = await fetchCitiesByState({ variables: { state: state.id } });
          return (response?.data?.citiesByState || []).map((city) => ({
            value: String(city.id),
            label: `${city.cityName} (${state.stateName})`,
            cityName: city.cityName,
          }));
        }));
        const options = cityGroups.flat().sort((a, b) => a.cityName.localeCompare(b.cityName));
        cachedCityOptions = options;
        if (isMounted) setCityOptions(options);
      } catch (error) {
        console.error('Error loading city filters:', error);
      } finally {
        if (isMounted) setCitiesLoading(false);
      }
    };

    loadCities();
    return () => { isMounted = false; };
  }, [fetchCitiesByState, statesData]);

  // Fetch delivery routes from API
  const fetchDeliveryRoutes = useCallback(async () => {
    try {
      setLoading(true);

      const apiBaseUrl = getApiBaseUrl();

      // If user selected multiple origin cities or multiple destination cities:
      if (originFilter.length > 1 || destinationFilter.length > 1) {
        let promises = [];
        if (originFilter.length > 0) {
          promises = originFilter.map((city) => {
            const p = new URLSearchParams({
              pageNumber: '0',
              pageSize: '100',
              originCityId: String(city.value),
            });
            if (destinationFilter.length === 1) {
              p.set('destinationCityId', String(destinationFilter[0].value));
            }
            return axios.get(`${apiBaseUrl}/delivery-rate/v2/routes?${p.toString()}`, {
              headers: { Authorization: `Bearer ${auth?.data?.token}` },
            });
          });
        } else {
          promises = destinationFilter.map((city) => {
            const p = new URLSearchParams({
              pageNumber: '0',
              pageSize: '100',
              destinationCityId: String(city.value),
            });
            return axios.get(`${apiBaseUrl}/delivery-rate/v2/routes?${p.toString()}`, {
              headers: { Authorization: `Bearer ${auth?.data?.token}` },
            });
          });
        }

        const responses = await Promise.all(promises);
        let allRows = responses.flatMap((res) => getRouteRows(res?.data));

        if (originFilter.length > 0 && destinationFilter.length > 1) {
          const destIds = new Set(destinationFilter.map((d) => String(d.value)));
          allRows = allRows.filter((r) =>
            destIds.has(String(r.destinationCityId ?? r.destinationCity?.id))
          );
        }

        const uniqueMap = new Map();
        allRows.forEach((r) => {
          if (r?.id != null) uniqueMap.set(r.id, r);
        });
        const combined = Array.from(uniqueMap.values());
        const total = combined.length;
        const pages = Math.ceil(total / filters.pageSize) || 1;
        const start = (filters.pageNo - 1) * filters.pageSize;
        const pageRows = combined.slice(start, start + filters.pageSize);

        setData(pageRows);
        setTotalCount(total);
        setTotalPages(pages);
      } else {
        const params = new URLSearchParams({
          pageNumber: String(filters.pageNo - 1),
          pageSize: String(filters.pageSize),
        });
        if (originFilter.length === 1) {
          params.set('originCityId', String(originFilter[0].value));
        }
        if (destinationFilter.length === 1) {
          params.set('destinationCityId', String(destinationFilter[0].value));
        }

        const response = await axios.get(
          `${apiBaseUrl}/delivery-rate/v2/routes?${params.toString()}`,
          {
            headers: {
              Authorization: `Bearer ${auth?.data?.token}`,
            },
          },
        );

        const rows = getRouteRows(response?.data);
        const nextTotalCount = getRouteTotalCount(response?.data, rows);
        const nextTotalPages = getRouteTotalPages(
          response?.data,
          filters.pageSize,
          nextTotalCount,
        );

        if (rows) {
          setData(rows);
          setTotalCount(nextTotalCount);
          setTotalPages(nextTotalPages);
        } else {
          toast.error('Failed to fetch delivery routes');
        }
      }
    } catch (error) {
      console.error('Error fetching delivery routes:', error);
      toast.error(
        error?.response?.data?.message || 'Failed to fetch delivery routes',
      );
    } finally {
      setLoading(false);
    }
  }, [auth?.data?.token, filters.pageNo, filters.pageSize, originFilter, destinationFilter]);

  useEffect(() => {
    if (auth?.data?.token) {
      fetchDeliveryRoutes();
    }
  }, [auth?.data?.token, fetchDeliveryRoutes]);

  const handlePageChange = ({ pageSize, pageNo }) => {
    setFilters({
      ...filters,
      pageNo,
      pageSize,
    });
  };

  const handleOriginChange = (selected) => {
    setOriginFilter(selected || []);
    setFilters((previous) => ({ ...previous, pageNo: 1 }));
  };

  const handleDestinationChange = (selected) => {
    setDestinationFilter(selected || []);
    setFilters((previous) => ({ ...previous, pageNo: 1 }));
  };

  const handleImportClick = useCallback(() => {
    setIsImportModalOpen(true);
  }, []);

  const handleExport = useCallback(async () => {
    try {
      setExporting(true);
      const apiBaseUrl = getApiBaseUrl();
      const params = new URLSearchParams();
      if (originFilter.length === 1) {
        params.set('originCityId', String(originFilter[0].value));
      }
      if (destinationFilter.length === 1) {
        params.set('destinationCityId', String(destinationFilter[0].value));
      }
      const qs = params.toString() ? `?${params.toString()}` : '';
      const response = await axios.get(
        `${apiBaseUrl}/delivery-rate/v2/routes/export${qs}`,
        {
          responseType: 'blob',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${auth?.data?.token}`,
          },
        },
      );

      downloadBlob(response.data, `delivery-routes-${Date.now()}.xlsx`);
    } catch (error) {
      console.error('Error exporting routes:', error);
      toast.error(error?.response?.data?.message || 'Failed to export routes');
    } finally {
      setExporting(false);
    }
  }, [auth?.data?.token, originFilter, destinationFilter]);

  const columns = useMemo(() => getListColumns(setRouteToEdit), []);

  const filteredData = useMemo(() => {
    if (!originFilter.length && !destinationFilter.length) return data;
    const matches = (route, selectedCities, idKey, nameKey) => !selectedCities.length || selectedCities.some((city) => (
      String(route[idKey]) === String(city.value)
      || String(route[nameKey] || '').toLowerCase() === String(city.cityName || '').toLowerCase()
    ));

    const result = data.filter((route) => (
      matches(route, originFilter, 'originCityId', 'originCityName')
      && matches(route, destinationFilter, 'destinationCityId', 'destinationCityName')
    ));
    return result.length > 0 ? result : data;
  }, [data, originFilter, destinationFilter]);

  const headerFilterRow = (
    <>
      <th aria-hidden="true" />
      <th>
        <div className="list-tab-inline-filter">
          <CityCheckboxFilter
            placeholder="Search origin"
            options={cityOptions}
            selectedValues={originFilter}
            onApply={handleOriginChange}
            isLoading={statesLoading || citiesLoading}
          />
        </div>
      </th>
      <th>
        <div className="list-tab-inline-filter">
          <CityCheckboxFilter
            placeholder="Search destination"
            options={cityOptions}
            selectedValues={destinationFilter}
            onApply={handleDestinationChange}
            isLoading={statesLoading || citiesLoading}
          />
        </div>
      </th>
      <th colSpan="8" aria-hidden="true" />
    </>
  );

  // Set buttons in page header next to tabs
  useEffect(() => {
    if (routeToEdit) {
      if (setHeaderActions) {
        setHeaderActions(
          <button type="button" className="sr-reset-btn list-tab-back-button" onClick={() => setRouteToEdit(null)}>
            ‹&nbsp; Back to List
          </button>,
        );
      }
      return () => {
        if (setHeaderActions) setHeaderActions(null);
      };
    }

    if (isAddingRoute) {
      if (setHeaderActions) {
        setHeaderActions(
          <button type="button" className="sr-reset-btn list-tab-back-button" onClick={() => setIsAddingRoute(false)}>
            ‹&nbsp; Back to List
          </button>,
        );
      }
      return () => {
        if (setHeaderActions) setHeaderActions(null);
      };
    }

    if (setHeaderActions) {
      setHeaderActions(
        <div className="list-tab-actions">
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => setIsAddingRoute(true)}
          >
            + Add
          </button>
          <button
            type="button"
            className="btn btn-outline-primary"
            onClick={handleImportClick}
            disabled={!auth?.data?.token}
          >
            Import
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleExport}
            disabled={exporting || !auth?.data?.token}
          >
            {exporting ? 'Exporting...' : 'Export'}
          </button>
        </div>,
      );
    }
    return () => {
      if (setHeaderActions) setHeaderActions(null);
    };
  }, [setHeaderActions, handleImportClick, handleExport, exporting, auth?.data?.token, routeToEdit, isAddingRoute]);

  return (
    <div className="mx-wrap list-tab-wrapper">
      {routeToEdit ? (
        <MatrixDetailView
          origin={routeToEdit.originCityName}
          dest={routeToEdit.destinationCityName}
          originCityId={routeToEdit.originCityId}
          destinationCityId={routeToEdit.destinationCityId}
          routeId={routeToEdit.id}
        />
      ) : isAddingRoute ? (
        <AddListView
          onBack={() => setIsAddingRoute(false)}
          onSuccess={() => {
            setIsAddingRoute(false);
            fetchDeliveryRoutes();
          }}
          cityOptions={cityOptions}
        />
      ) : (
        <>
          <FSBTable
            data={filteredData}
            columns={columns}
            headerSubRow={headerFilterRow}
            totalCount={totalCount}
            totalPages={totalPages}
            currentPageNo={filters.pageNo}
            pageSize={filters.pageSize}
            keyField="id"
            showSearch={false}
            showFilters={false}
            showDelete={false}
            showPagination
            remote
            striped={false}
            hover={false}
            bordered={false}
            wrapperClass="table-responsive word-break"
            handlePageChange={handlePageChange}
            loading={loading}
          />
        </>
      )}

      {/* Import CSV / Excel Modal */}
      <ImportRoutesModal
        isOpen={isImportModalOpen}
        toggle={() => setIsImportModalOpen(false)}
        onSuccess={() => {
          setFilters((prev) => ({ ...prev, pageNo: 1 }));
          fetchDeliveryRoutes();
        }}
        token={auth?.data?.token}
      />
    </div>
  );
};

export default ListTab;
